import { useState } from "react";
import marketDataService from "../services/marketDataService";
import { LivePriceInfo, LivePricesResponse, PriceSource } from "../types/contract";
import { StockPriceAllSourceEntry } from "../types/marketData";
import { formatNumber } from "../utils/numberUtils";

/**
 * Convert market-data API response (numeric keys, StockPriceAllSourceEntry[])
 * to the LivePricesResponse shape (string keys, LivePriceInfo[]) expected by
 * all downstream consumers. This keeps the boundary conversion in one place.
 */
function toStringKeyedResponse(
  data: Record<number, StockPriceAllSourceEntry[]>
): LivePricesResponse {
  const result: LivePricesResponse = {};
  for (const [key, entries] of Object.entries(data)) {
    result[key] = entries.map((e) => ({
      source: e.source,
      isSourceMapped: e.isSourceMapped,
      price: e.price,
    }));
  }
  return result;
}

export const useContractPrices = () => {
  const [livePrices, setLivePrices] = useState<LivePricesResponse>({});
  const [fetchingPrices, setFetchingPrices] = useState<Set<number>>(new Set());
  const [fetchingAllPrices, setFetchingAllPrices] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);

  const fetchPriceForContract = async (conId: number) => {
    setFetchingPrices(prev => new Set(prev).add(conId));
    try {
      const response = await marketDataService.getStockPricesAll([conId]);
      setLivePrices(prev => ({ ...prev, ...toStringKeyedResponse(response) }));
    } catch (err) {
      setPriceError(err instanceof Error ? err.message : "Failed to fetch price");
    } finally {
      setFetchingPrices(prev => {
        const next = new Set(prev);
        next.delete(conId);
        return next;
      });
    }
  };

  const fetchAllPrices = async (conIds: number[]) => {
    if (conIds.length === 0) return;
    setFetchingAllPrices(true);
    setFetchingPrices(new Set(conIds));

    const BATCH_SIZE = 5;

    try {
      for (let i = 0; i < conIds.length; i += BATCH_SIZE) {
        const batch = conIds.slice(i, i + BATCH_SIZE);
        try {
          const response = await marketDataService.getStockPricesAll(batch);
          setLivePrices(prev => ({ ...prev, ...toStringKeyedResponse(response) }));
        } catch (innerErr) {
          console.error(`Failed to fetch batch ${i / BATCH_SIZE + 1}`, innerErr);
        }

        await new Promise(r => setTimeout(r, 200)); // 200ms delay between batches
      }
    } catch (err) {
      setPriceError(err instanceof Error ? err.message : "Failed to fetch prices");
    } finally {
      setFetchingAllPrices(false);
      setFetchingPrices(new Set());
    }
  };

  const getPriceDisplay = (conId: number, source: PriceSource) => {
    const priceData = livePrices[String(conId)];
    if (!priceData) return null;
    const info = priceData.find((p: LivePriceInfo) => p.source === source);
    if (!info) return null;
    // Show raw numeric value as-is (deliberate behavior change -- no "Error" fallback)
    return { value: formatNumber(info.price), error: false };
  };

  return {
    livePrices,
    fetchingPrices,
    fetchingAllPrices,
    priceError,
    fetchPriceForContract,
    fetchAllPrices,
    getPriceDisplay
  };
};
